import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X, ArrowUp, ArrowDown } from "lucide-react"

import { cn } from "../../lib/utils"
import useOverlayBack from "../../hooks/useOverlayBack"

/**
 * Every controlled dialog registers with the back-button stack, so one back
 * press (phone button or browser arrow) closes the dialog instead of
 * unloading the app and losing what was being edited.
 */
const Dialog = ({ open, onOpenChange, ...props }) => {
  useOverlayBack(open, onOpenChange);
  return <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} {...props} />;
};

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * Watches a dialog's scrollport so the jump arrows only appear when there
 * is somewhere to jump to.
 *
 * Two traps this works around: the scrollport is sometimes an inner
 * element rather than the dialog box itself, and a ResizeObserver on the
 * box never fires when only its scrollHeight grows (a max-height dialog
 * filling up with fields), so the content is watched too.
 */
const findScrollport = (root) => {
  if (!root) return null;
  if (root.scrollHeight - root.clientHeight > 48) return root;
  let best = null;
  let bestSlack = 48;
  root.querySelectorAll("*").forEach((el) => {
    const slack = el.scrollHeight - el.clientHeight;
    if (slack <= bestSlack) return
    const overflow = window.getComputedStyle(el).overflowY
    if (overflow === "auto" || overflow === "scroll") {
      best = el
      bestSlack = slack
    }
  })
  return best
}

const useScrollport = (root) => {
  const portRef = React.useRef(null)
  const [state, setState] = React.useState({ scrollable: false, atTop: true, atBottom: false })

  React.useEffect(() => {
    if (!root) return

    const read = () => {
      const el = portRef.current
      if (!el) return setState({ scrollable: false, atTop: true, atBottom: false })
      const slack = el.scrollHeight - el.clientHeight
      setState({
        scrollable: slack > 48,
        atTop: el.scrollTop <= 8,
        atBottom: el.scrollTop >= slack - 8,
      })
    }

    const observer = new ResizeObserver(() => { portRef.current = findScrollport(root); read() })
    let frame = 0
    const watch = () => {
      // Debounced: a deep dialog fires a burst of mutations while it fills.
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        observer.disconnect()
        observer.observe(root)
        Array.from(root.children).forEach((child) => observer.observe(child))
        const cached = portRef.current
        const stillGood = cached && root.contains(cached) && cached.scrollHeight - cached.clientHeight > 48
        if (!stillGood) portRef.current = findScrollport(root)
        read()
      })
    }

    watch()
    const mutations = new MutationObserver(watch)
    mutations.observe(root, { childList: true, subtree: true })
    root.addEventListener("scroll", read, { passive: true, capture: true })

    return () => {
      if (frame) cancelAnimationFrame(frame)
      observer.disconnect()
      mutations.disconnect()
      root.removeEventListener("scroll", read, true)
    }
  }, [root])

  const jump = (edge) => {
    const el = portRef.current
    if (el) el.scrollTo({ top: edge === "top" ? 0 : el.scrollHeight, behavior: "smooth" })
  }

  return { ...state, jump }
}

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  // Radix mounts the content only once the dialog opens, and does so
  // without re-rendering this component — so the node has to arrive via
  // state, otherwise the scroll watcher below would only ever see null.
  const [content, setContent] = React.useState(null)
  const setRefs = React.useCallback((node) => {
    setContent(node)
    if (typeof ref === "function") ref(node)
    else if (ref) ref.current = node
  }, [ref])

  const { scrollable, atTop, atBottom, jump } = useScrollport(content)

  const arrowCls = "pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full border bg-background/95 shadow-md opacity-80 backdrop-blur transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-30 disabled:pointer-events-none"

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={setRefs}
        className={cn(
          // Viewport-safe defaults: cap size to the browser window and let
          // the content scroll if it overflows. This prevents tall dialogs
          // (Batch Payment picker, Edit Expense, Employee Form, etc.) from
          // clipping their footer buttons on short screens / mobile.
          "fixed left-[50%] top-[50%] z-50 grid grid-cols-[minmax(0,1fr)] w-[calc(100vw-2rem)] max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        {...props}>
        {/* Floating close button — sticky so it stays reachable while a
            tall dialog body scrolls. Users who cannot see a close control
            reach for the browser Back button, which loses their edits.
            The jump arrows join it once the body is long enough to scroll. */}
        <div data-scrollable={String(scrollable)} className="sticky top-0 z-50 h-0 -mb-4 flex flex-col items-end gap-1.5 pointer-events-none">
          <DialogPrimitive.Close
            className="pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full border bg-background/95 shadow-md opacity-80 backdrop-blur transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
            data-testid="dialog-floating-close"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
          {scrollable && (
            <>
              <button type="button" onClick={() => jump("top")} disabled={atTop}
                title="Scroll to top" aria-label="Scroll to top"
                data-testid="dialog-scroll-top" className={arrowCls}>
                <ArrowUp className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => jump("bottom")} disabled={atBottom}
                title="Scroll to bottom" aria-label="Scroll to bottom"
                data-testid="dialog-scroll-bottom" className={arrowCls}>
                <ArrowDown className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 pr-8 text-center sm:pr-0 sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
